import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";
import {
  Search, Plus, Sparkles, X, ExternalLink, Mail, Phone,
  Linkedin, MapPin, Send, ChevronRight, Star,
  Wand2, Copy, Trash2, RefreshCw, Upload,
} from "lucide-react";

// Sort + column-visibility helpers
const SORT_OPTIONS = {
  name: (a, b) => (a.name || "").localeCompare(b.name || ""),
  fit_score: (a, b) => (a.fit_score || 0) - (b.fit_score || 0),
  priority: (a, b) => ({ A: 0, B: 1, C: 2 }[a.priority || "C"] - { A: 0, B: 1, C: 2 }[b.priority || "C"]),
  hq: (a, b) => (a.hq_country || "").localeCompare(b.hq_country || ""),
  research_status: (a, b) => (a.research_status || "").localeCompare(b.research_status || ""),
};
const ALL_COLUMNS = ["investor", "type_hq", "sector_stage", "fit", "priority", "status"];
const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS;
const COLUMN_LABEL = {
  investor: "Investor", type_hq: "Type · HQ", sector_stage: "Sector / Stage",
  fit: "Fit", priority: "Priority", status: "Status",
};

function applySort(items, sortKey, sortDir) {
  if (!sortKey || !SORT_OPTIONS[sortKey]) return items;
  const sorted = [...items].sort(SORT_OPTIONS[sortKey]);
  return sortDir === "desc" ? sorted.reverse() : sorted;
}

// ────────────────────────────────────────────────────────────────────
// Pure filter helper (kept outside the component for lint/clarity)
// ────────────────────────────────────────────────────────────────────
function applyFilters(investors, { search, filterPriority, filterStatus, filterDeal }) {
  return investors.filter((i) => {
    if (filterPriority && i.priority !== filterPriority) return false;
    if (filterStatus && i.research_status !== filterStatus) return false;
    if (filterDeal && !(i.deal_ids || []).includes(filterDeal)) return false;
    if (search) {
      const s = search.toLowerCase();
      const hay = `${i.name} ${i.sector_fit} ${i.geo_fit} ${i.category_bucket} ${i.hq_city} ${i.hq_country}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });
}

// ────────────────────────────────────────────────────────────────────
// Shared helpers
// ────────────────────────────────────────────────────────────────────
const PRIORITY_PILL = {
  A: "bg-gold text-navy",
  B: "bg-navy text-gold",
  C: "bg-navy/10 text-navy/70",
};
const STATUS_COLOR = {
  "Not Started": "bg-navy/5 text-navy/60",
  Sent: "bg-blue-50 text-blue-700",
  Responded: "bg-amber-50 text-amber-800",
  "Meeting Booked": "bg-emerald-50 text-emerald-800",
  Passed: "bg-navy/5 text-navy/40",
  Won: "bg-gold text-navy",
};

function PageHeader({ overline, title, right }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline text-gold-deep">{overline}</div>
          <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
          <div className="gold-rule mt-4" />
        </div>
        {right}
      </div>
    </div>
  );
}

function PriorityPill({ p }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${PRIORITY_PILL[p] || PRIORITY_PILL.C}`}>
      {p === "A" ? "A · High" : p === "B" ? "B · Medium" : "C · Low"}
    </span>
  );
}

function StatPill({ label, value, accent }) {
  return (
    <div className={`bg-white border ${accent ? "border-gold" : "border-navy/10"} p-5 flex-1 min-w-[140px]`}>
      <div className="overline text-navy/60">{label}</div>
      <div className="font-serif text-3xl text-navy mt-1">{value}</div>
    </div>
  );
}

function SortableTh({ children, sortKey, current, dir, onClick }) {
  const active = current === sortKey;
  return (
    <th
      onClick={onClick}
      data-testid={`investors-sort-${sortKey}`}
      className="px-4 py-3 overline text-navy/60 cursor-pointer select-none hover:text-navy"
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <span className={`text-[8px] ${active ? "text-gold-deep" : "text-navy/25"}`}>
          {active ? (dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </span>
    </th>
  );
}

// ────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────
export default function Investors() {
  const [searchParams] = useSearchParams();
  const [investors, setInvestors] = useState([]);
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDeal, setFilterDeal] = useState(searchParams.get("deal") || "");
  const [sortKey, setSortKey] = useState("priority");
  const [sortDir, setSortDir] = useState("asc");
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = localStorage.getItem("jp_investor_cols_v1");
      if (saved) return JSON.parse(saved);
    } catch { /* noop */ }
    return DEFAULT_VISIBLE_COLUMNS;
  });
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  };

  const toggleColumn = (col) => {
    setVisibleCols((cols) => {
      const next = cols.includes(col)
        ? cols.filter((c) => c !== col)
        : [...cols, col].sort((a, b) => ALL_COLUMNS.indexOf(a) - ALL_COLUMNS.indexOf(b));
      try { localStorage.setItem("jp_investor_cols_v1", JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  const visible = (c) => visibleCols.includes(c);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, s, d] = await Promise.all([
        api.get("/investors"),
        api.get("/investors/stats"),
        api.get("/deals"),
      ]);
      setInvestors(inv.data);
      setStats(s.data);
      setDeals(d.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [inv, s, d] = await Promise.all([
          api.get("/investors"),
          api.get("/investors/stats"),
          api.get("/deals"),
        ]);
        if (cancelled) return;
        setInvestors(inv.data);
        setStats(s.data);
        setDeals(d.data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = applySort(
    applyFilters(investors, { search, filterPriority, filterStatus, filterDeal }),
    sortKey,
    sortDir,
  );

  const seed = async () => {
    if (!(await confirmDialog({ title: "Re-seed the curated investor list?", description: "Loads bundled Investorlist data. Investors with names that already exist will be skipped — no overwrites.", confirmLabel: "Re-seed" }))) return;
    try {
      const { data } = await api.post("/investors/seed");
      toast.success(`Seeded ${data.created} new investors (skipped ${data.skipped_already_existing} duplicates)`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Seed failed");
    }
  };

  return (
    <div data-testid="investors-page">
      <PageHeader
        overline="Investor Mapping"
        title="Outreach Command Center"
        right={
          <div className="flex gap-2">
            <button
              onClick={() => setShowAI(true)}
              data-testid="investors-ai-shortlist"
              className="bg-navy text-gold px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2"
            >
              <Sparkles className="h-3.5 w-3.5" /> AI shortlist for deal
            </button>
            <button
              onClick={() => setShowImport(true)}
              data-testid="investors-import-xlsx"
              className="border border-navy/30 text-navy/80 px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold flex items-center gap-2"
            >
              <Upload className="h-3.5 w-3.5" /> Import XLSX
            </button>
            <button
              onClick={seed}
              data-testid="investors-seed"
              className="border border-navy/30 text-navy/80 px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold flex items-center gap-2"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Re-seed
            </button>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        {/* KPI strip */}
        {stats && (
          <div className="flex flex-wrap gap-3" data-testid="investors-kpis">
            <StatPill label="Total Investors" value={stats.total} />
            <StatPill label="A · High Priority" value={stats.by_priority.A} accent />
            <StatPill label="B · Medium" value={stats.by_priority.B} />
            <StatPill label="Enriched" value={stats.enriched} />
            <StatPill label="Outreach Sent" value={stats.outreach_total} />
            <StatPill label="Responses" value={stats.outreach_responded} />
            <StatPill label="Meetings Booked" value={stats.outreach_meetings} accent />
          </div>
        )}

        {/* Filter bar */}
        <div className="bg-white border border-navy/10 p-4 flex flex-wrap items-center gap-3" data-testid="investors-filters">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy/40" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, sector, region…"
              data-testid="investors-search"
              className="w-full pl-10 pr-3 py-2 border border-navy/15 bg-transparent focus:outline-none focus:border-gold text-sm"
            />
          </div>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} data-testid="investors-filter-priority"
            className="border border-navy/15 bg-transparent py-2 px-3 text-sm focus:outline-none focus:border-gold">
            <option value="">All priorities</option>
            <option value="A">A — High</option>
            <option value="B">B — Medium</option>
            <option value="C">C — Low</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="investors-filter-status"
            className="border border-navy/15 bg-transparent py-2 px-3 text-sm focus:outline-none focus:border-gold">
            <option value="">Any research status</option>
            <option value="Enriched">Enriched</option>
            <option value="Partially enriched">Partially enriched</option>
            <option value="Sourced">Sourced</option>
          </select>
          <select value={filterDeal} onChange={(e) => setFilterDeal(e.target.value)} data-testid="investors-filter-deal"
            className="border border-navy/15 bg-transparent py-2 px-3 text-sm focus:outline-none focus:border-gold">
            <option value="">All deals</option>
            {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {(search || filterPriority || filterStatus || filterDeal) && (
            <button
              onClick={() => { setSearch(""); setFilterPriority(""); setFilterStatus(""); setFilterDeal(""); }}
              data-testid="investors-clear-filters"
              className="text-xs uppercase tracking-widest text-navy/60 hover:text-gold-deep flex items-center gap-1"
            >
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          )}
          <div className="text-xs text-navy/50 ml-auto">
            {filtered.length} of {investors.length} shown
          </div>
          <div className="relative">
            <button
              onClick={() => setColMenuOpen((o) => !o)}
              data-testid="investors-cols-toggle"
              className="text-xs uppercase tracking-widest text-navy/60 hover:text-gold-deep px-3 py-1.5 border border-navy/15"
            >
              Columns ▾
            </button>
            {colMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setColMenuOpen(false)} />
                <div
                  data-testid="investors-cols-menu"
                  className="absolute right-0 top-full mt-1 z-20 bg-white border border-navy/15 shadow-lg p-3 min-w-[200px]"
                >
                  <div className="overline text-navy/55 mb-2">Show columns</div>
                  {ALL_COLUMNS.map((c) => (
                    <label key={c} className="flex items-center gap-2 py-1.5 cursor-pointer text-sm hover:text-gold-deep">
                      <input
                        type="checkbox"
                        checked={visible(c)}
                        onChange={() => toggleColumn(c)}
                        data-testid={`investors-col-${c}`}
                        className="h-3.5 w-3.5 accent-gold"
                      />
                      <span className="text-navy/85">{COLUMN_LABEL[c]}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Investor table */}
        <div className="bg-white border border-navy/10 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-navy/40">Loading investors…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-navy/40 italic">
              No investors match your filters.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="investors-table">
              <thead>
                <tr className="bg-cream/50 border-b border-navy/10 text-left">
                  {visible("investor") && (
                    <SortableTh sortKey="name" current={sortKey} dir={sortDir} onClick={() => toggleSort("name")}>
                      Investor
                    </SortableTh>
                  )}
                  {visible("type_hq") && (
                    <SortableTh sortKey="hq" current={sortKey} dir={sortDir} onClick={() => toggleSort("hq")}>
                      Type · HQ
                    </SortableTh>
                  )}
                  {visible("sector_stage") && (
                    <th className="px-4 py-3 overline text-navy/60">Sector / Stage</th>
                  )}
                  {visible("fit") && (
                    <SortableTh sortKey="fit_score" current={sortKey} dir={sortDir} onClick={() => toggleSort("fit_score")}>
                      Fit
                    </SortableTh>
                  )}
                  {visible("priority") && (
                    <SortableTh sortKey="priority" current={sortKey} dir={sortDir} onClick={() => toggleSort("priority")}>
                      Priority
                    </SortableTh>
                  )}
                  {visible("status") && (
                    <SortableTh sortKey="research_status" current={sortKey} dir={sortDir} onClick={() => toggleSort("research_status")}>
                      Status
                    </SortableTh>
                  )}
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr
                    key={i.id}
                    onClick={() => setSelected(i)}
                    data-testid={`investor-row-${i.id}`}
                    className="border-b border-navy/5 last:border-b-0 hover:bg-cream/40 cursor-pointer"
                  >
                    {visible("investor") && (
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-navy">{i.name}</div>
                        <div className="text-xs text-navy/50">{i.category_bucket}</div>
                      </td>
                    )}
                    {visible("type_hq") && (
                      <td className="px-4 py-3 align-top text-navy/75">
                        <div className="text-xs">{i.investor_type}</div>
                        <div className="text-xs text-navy/50 flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" /> {i.hq_city || "—"}{i.hq_country ? `, ${i.hq_country.split(",")[0]}` : ""}
                        </div>
                      </td>
                    )}
                    {visible("sector_stage") && (
                      <td className="px-4 py-3 align-top text-navy/75 max-w-[260px]">
                        <div className="text-xs truncate" title={i.sector_fit}>{i.sector_fit || "—"}</div>
                        <div className="text-[11px] text-navy/50 truncate" title={i.stage_fit}>{i.stage_fit || "—"}</div>
                    </td>
                    )}
                    {visible("fit") && (
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-1 text-gold-deep">
                        {Array.from({ length: 5 }).map((_, idx) => (
                          <Star key={idx} className={`h-3 w-3 ${idx < (i.fit_score || 0) ? "fill-current" : ""}`} />
                        ))}
                      </div>
                    </td>
                    )}
                    {visible("priority") && (
                      <td className="px-4 py-3 align-top"><PriorityPill p={i.priority} /></td>
                    )}
                    {visible("status") && (
                      <td className="px-4 py-3 align-top">
                        <span className="text-[10px] uppercase tracking-widest text-navy/60">
                          {i.research_status || "Sourced"}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 align-top text-navy/30">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail slide-over */}
      {selected && (
        <InvestorDetail
          investor={selected}
          deals={deals}
          onClose={() => setSelected(null)}
          onChange={async (fresh) => {
            await load();
            if (fresh) setSelected(fresh);
          }}
        />
      )}

      {/* AI shortlist modal */}
      {showAI && (
        <AIShortlistModal
          deals={deals}
          onClose={() => setShowAI(false)}
          onLinked={load}
        />
      )}

      {/* Import XLSX modal */}
      {showImport && (
        <ImportXlsxModal
          onClose={() => setShowImport(false)}
          onImported={load}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Detail slide-over: investor profile + outreach log + AI draft button
// ────────────────────────────────────────────────────────────────────
function InvestorDetail({ investor, deals, onClose, onChange }) {
  const [tab, setTab] = useState("profile");
  const [outreach, setOutreach] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [newOutOpen, setNewOutOpen] = useState(false);

  const loadOutreach = async () => {
    const { data } = await api.get(`/investors/${investor.id}/outreach`);
    setOutreach(data);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get(`/investors/${investor.id}/outreach`);
      if (!cancelled) setOutreach(data);
    })();
    return () => { cancelled = true; };
  }, [investor.id]);

  const linkDeal = async (deal_id) => {
    await api.post(`/investors/${investor.id}/link-deal/${deal_id}`);
    const { data } = await api.get(`/investors/${investor.id}`);
    onChange?.(data);
    toast.success("Linked to deal");
  };
  const unlinkDeal = async (deal_id) => {
    await api.post(`/investors/${investor.id}/unlink-deal/${deal_id}`);
    const { data } = await api.get(`/investors/${investor.id}`);
    onChange?.(data);
  };
  const linkedDeals = (investor.deal_ids || [])
    .map((id) => deals.find((d) => d.id === id))
    .filter(Boolean);
  const unlinkedDeals = deals.filter((d) => !(investor.deal_ids || []).includes(d.id));

  return (
    <div className="fixed inset-0 z-50 bg-navy/40 flex justify-end" onClick={onClose} data-testid="investor-detail">
      <div
        className="bg-cream w-full max-w-3xl h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-cream border-b border-navy/10 px-8 py-5 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <PriorityPill p={investor.priority} />
              <span className="text-[10px] uppercase tracking-widest text-navy/60">{investor.research_status}</span>
            </div>
            <h2 className="font-serif text-3xl text-navy mt-2 truncate">{investor.name}</h2>
            <div className="text-sm text-navy/65 mt-1">{investor.investor_type}</div>
            <div className="text-xs text-navy/50 mt-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {investor.hq_city}{investor.hq_country ? `, ${investor.hq_country}` : ""}
            </div>
          </div>
          <button onClick={onClose} data-testid="investor-detail-close" className="text-navy/40 hover:text-navy">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-8 pt-4 border-b border-navy/10 flex gap-6">
          {["profile", "outreach", "deals"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`investor-tab-${t}`}
              className={`text-xs uppercase tracking-widest pb-3 transition-colors ${
                tab === t ? "text-gold-deep border-b-2 border-gold" : "text-navy/50 hover:text-navy"
              }`}
            >
              {t === "outreach" ? `Outreach (${outreach.length})` : t === "deals" ? `Linked Deals (${linkedDeals.length})` : t}
            </button>
          ))}
        </div>

        <div className="p-8 space-y-8">
          {tab === "profile" && (
            <>
              {/* Quick contact bar */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="investor-contact-bar">
                {investor.website && (
                  <a href={investor.website} target="_blank" rel="noreferrer" className="bg-white border border-navy/10 p-3 hover:border-gold flex items-center gap-2">
                    <ExternalLink className="h-3.5 w-3.5 text-gold-deep" />
                    <span className="truncate text-navy/85">Website</span>
                  </a>
                )}
                {investor.linkedin_url && (
                  <a href={investor.linkedin_url} target="_blank" rel="noreferrer" className="bg-white border border-navy/10 p-3 hover:border-gold flex items-center gap-2">
                    <Linkedin className="h-3.5 w-3.5 text-gold-deep" />
                    <span className="truncate text-navy/85">LinkedIn</span>
                  </a>
                )}
                {investor.email && (
                  <a href={`mailto:${investor.email}`} className="bg-white border border-navy/10 p-3 hover:border-gold flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-gold-deep" />
                    <span className="truncate text-navy/85">{investor.email}</span>
                  </a>
                )}
                {investor.phone && (
                  <a href={`tel:${investor.phone}`} className="bg-white border border-navy/10 p-3 hover:border-gold flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 text-gold-deep" />
                    <span className="truncate text-navy/85">{investor.phone}</span>
                  </a>
                )}
              </div>

              {/* Fit grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FitField label="Stage Fit" value={investor.stage_fit} />
                <FitField label="Sector Fit" value={investor.sector_fit} />
                <FitField label="Geographic Fit" value={investor.geo_fit} />
                <FitField label="Typical Check Size" value={investor.check_size} />
                <FitField label="Fit for Mandate" value={`${investor.fit_for_mandate} (${investor.fit_score}/5)`} />
                <FitField label="Category" value={investor.category_bucket} />
              </div>

              {/* Contact persons */}
              {(investor.contact_persons || []).length > 0 && (
                <div>
                  <div className="overline text-gold-deep mb-3">Contact persons</div>
                  <div className="space-y-2">
                    {investor.contact_persons.map((cp, idx) => (
                      <div key={idx} className="bg-white border border-navy/10 p-3 flex items-start justify-between gap-3" data-testid={`contact-person-${idx}`}>
                        <div>
                          <div className="font-medium text-navy">{cp.name}</div>
                          <div className="text-xs text-navy/55">{cp.title}</div>
                        </div>
                        {cp.linkedin_url && (
                          <a href={cp.linkedin_url} target="_blank" rel="noreferrer" className="text-gold-deep hover:underline text-xs flex items-center gap-1">
                            <Linkedin className="h-3 w-3" /> LinkedIn
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Next action / notes */}
              {investor.next_action && (
                <div className="bg-navy text-cream border-l-2 border-gold p-5" data-testid="next-action-box">
                  <div className="overline text-gold mb-1">Next action</div>
                  <p className="text-sm text-cream/90 font-light leading-relaxed">{investor.next_action}</p>
                </div>
              )}
              {investor.notes && (
                <div>
                  <div className="overline text-gold-deep mb-2">Notes</div>
                  <p className="text-sm text-navy/80 font-light leading-relaxed whitespace-pre-wrap">{investor.notes}</p>
                </div>
              )}

              {/* Sources */}
              {(investor.sources || []).length > 0 && (
                <div>
                  <div className="overline text-gold-deep mb-2">Sources ({investor.source_confidence} confidence)</div>
                  <ul className="text-xs space-y-1">
                    {investor.sources.map((url, idx) => (
                      <li key={idx}>
                        <a href={url} target="_blank" rel="noreferrer" className="text-gold-deep hover:underline truncate inline-block max-w-full">
                          {url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-navy/10">
                <button
                  onClick={() => setAiOpen(true)}
                  data-testid="ai-draft-outreach-btn"
                  className="bg-navy text-gold px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2"
                >
                  <Wand2 className="h-3.5 w-3.5" /> AI draft outreach
                </button>
                <button
                  onClick={() => setNewOutOpen(true)}
                  data-testid="log-outreach-btn"
                  className="border border-navy text-navy px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold flex items-center gap-2"
                >
                  <Send className="h-3.5 w-3.5" /> Log outreach
                </button>
              </div>
            </>
          )}

          {tab === "outreach" && (
            <OutreachPanel
              investor={investor}
              outreach={outreach}
              deals={deals}
              onReload={loadOutreach}
              onAIDraft={() => setAiOpen(true)}
              onLog={() => setNewOutOpen(true)}
            />
          )}

          {tab === "deals" && (
            <DealsTab
              linkedDeals={linkedDeals}
              unlinkedDeals={unlinkedDeals}
              onLink={linkDeal}
              onUnlink={unlinkDeal}
            />
          )}
        </div>
      </div>

      {/* AI Draft modal */}
      {aiOpen && (
        <AIDraftOutreachModal
          investor={investor}
          deals={deals}
          onClose={() => setAiOpen(false)}
          onLogged={() => { loadOutreach(); setAiOpen(false); }}
        />
      )}

      {/* New outreach log modal */}
      {newOutOpen && (
        <LogOutreachModal
          investor={investor}
          deals={deals}
          initial={null}
          onClose={() => setNewOutOpen(false)}
          onSaved={() => { loadOutreach(); setNewOutOpen(false); }}
        />
      )}
    </div>
  );
}

function FitField({ label, value }) {
  return (
    <div className="bg-white border border-navy/10 p-4">
      <div className="overline text-navy/60">{label}</div>
      <div className="text-sm text-navy/85 mt-1">{value || "—"}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Outreach panel
// ────────────────────────────────────────────────────────────────────
function OutreachPanel({ investor, outreach, deals, onReload, onAIDraft, onLog }) {
  const [editing, setEditing] = useState(null);
  const remove = async (id) => {
    if (!(await confirmDialog({ title: "Delete this outreach record?", description: "This will permanently remove the touchpoint from the investor's history.", confirmLabel: "Delete", destructive: true }))) return;
    await api.delete(`/outreach/${id}`);
    onReload();
  };

  return (
    <div data-testid="outreach-panel">
      <div className="flex justify-end gap-2 mb-4">
        <button onClick={onAIDraft} className="bg-navy text-gold px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2" data-testid="outreach-ai-draft">
          <Wand2 className="h-3.5 w-3.5" /> AI draft
        </button>
        <button onClick={onLog} className="border border-navy text-navy px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold flex items-center gap-2" data-testid="outreach-log-new">
          <Plus className="h-3.5 w-3.5" /> Log outreach
        </button>
      </div>
      {outreach.length === 0 ? (
        <div className="bg-white border border-navy/10 p-12 text-center text-navy/40 italic">
          No outreach logged yet.
        </div>
      ) : (
        <div className="space-y-3">
          {outreach.map((o) => (
            <div key={o.id} className="bg-white border border-navy/10 p-4" data-testid={`outreach-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_COLOR[o.status] || "bg-navy/5 text-navy/60"}`}>{o.status}</span>
                    <span className="text-[10px] uppercase tracking-widest text-navy/50">{o.channel} · {o.direction}</span>
                    {o.contact_person && <span className="text-xs text-navy/65">→ {o.contact_person}</span>}
                  </div>
                  {o.subject && <div className="font-medium text-navy mt-2">{o.subject}</div>}
                  {o.body && <p className="text-sm text-navy/75 mt-1 whitespace-pre-wrap line-clamp-4">{o.body}</p>}
                  {o.response_summary && (
                    <div className="mt-2 text-xs">
                      <span className="overline text-gold-deep">Response: </span>
                      <span className="text-navy/75">{o.response_summary}</span>
                    </div>
                  )}
                  <div className="mt-2 text-[11px] text-navy/45">
                    {o.last_touch_at && `Last touch: ${(o.last_touch_at || "").slice(0,10)}`}
                    {o.next_touch_at && ` · Next: ${(o.next_touch_at || "").slice(0,10)}`}
                    {o.deal_id && (() => { const d = deals.find((x) => x.id === o.deal_id); return d ? ` · Deal: ${d.name}` : ""; })()}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-navy/40">
                  <button onClick={() => setEditing(o)} className="hover:text-navy" data-testid={`outreach-edit-${o.id}`}>Edit</button>
                  <button onClick={() => remove(o.id)} className="hover:text-destructive" data-testid={`outreach-delete-${o.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <LogOutreachModal
          investor={investor}
          deals={deals}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Deals tab
// ────────────────────────────────────────────────────────────────────
function DealsTab({ linkedDeals, unlinkedDeals, onLink, onUnlink }) {
  return (
    <div data-testid="investor-deals-tab" className="space-y-6">
      <div>
        <div className="overline text-gold-deep mb-3">Linked deals</div>
        {linkedDeals.length === 0 ? (
          <div className="text-sm italic text-navy/40">Not linked to any deal yet.</div>
        ) : (
          <div className="space-y-2">
            {linkedDeals.map((d) => (
              <div key={d.id} className="bg-white border border-navy/10 p-3 flex items-center justify-between" data-testid={`linked-deal-${d.id}`}>
                <div>
                  <div className="font-medium text-navy">{d.name}</div>
                  <div className="text-xs text-navy/50">{d.stage} · ${(d.ticket_size_usd || 0).toLocaleString()}</div>
                </div>
                <button onClick={() => onUnlink(d.id)} className="text-xs text-navy/50 hover:text-destructive uppercase tracking-widest" data-testid={`unlink-deal-${d.id}`}>
                  Unlink
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {unlinkedDeals.length > 0 && (
        <div>
          <div className="overline text-gold-deep mb-3">Link to another deal</div>
          <div className="space-y-2">
            {unlinkedDeals.map((d) => (
              <div key={d.id} className="bg-cream/40 border border-dashed border-navy/15 p-3 flex items-center justify-between">
                <div>
                  <div className="text-sm text-navy">{d.name}</div>
                  <div className="text-xs text-navy/50">{d.stage}</div>
                </div>
                <button onClick={() => onLink(d.id)} className="text-xs uppercase tracking-widest text-gold-deep hover:underline" data-testid={`link-deal-${d.id}`}>
                  + Link
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// AI Draft Outreach modal
// ────────────────────────────────────────────────────────────────────
function AIDraftOutreachModal({ investor, deals, onClose, onLogged }) {
  const defaultContact = (investor.contact_persons || [])[0]?.name || "";
  const [contactPerson, setContactPerson] = useState(defaultContact);
  const [dealId, setDealId] = useState((investor.deal_ids || [])[0] || "");
  const [tone, setTone] = useState("warm-intro");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [drafted, setDrafted] = useState(null);

  const draft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/investors/ai-draft-outreach", {
        investor_id: investor.id,
        deal_id: dealId || null,
        contact_person: contactPerson,
        tone,
        extra_context: extra,
      });
      setDrafted(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI draft failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(drafted.email);
    toast.success("Copied to clipboard");
  };

  const logAsSent = async () => {
    await api.post(`/investors/${investor.id}/outreach`, {
      deal_id: dealId || null,
      contact_person: contactPerson,
      channel: "Email",
      status: "Sent",
      direction: "Outbound",
      subject: drafted.subject,
      body: drafted.body,
      notes: `AI-drafted (${tone})`,
      last_touch_at: new Date().toISOString(),
    });
    toast.success("Logged as sent");
    onLogged();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/60 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-draft-modal">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-gold-deep">AI Outreach Draft</div>
            <h3 className="font-serif text-2xl text-navy mt-1">{investor.name}</h3>
          </div>
          <button onClick={onClose} className="text-navy/40 hover:text-navy" data-testid="ai-draft-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="gold-rule mb-5" />

        {!drafted ? (
          <div className="space-y-4">
            <div>
              <label className="overline text-navy/60">Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="ai-tone"
                className="w-full mt-2 border border-navy/15 bg-transparent py-2 px-3 focus:outline-none focus:border-gold text-sm">
                <option value="warm-intro">Warm intro</option>
                <option value="cold">Cold outreach</option>
                <option value="follow-up">Follow-up (no response yet)</option>
                <option value="thank-you">Post-meeting thank-you</option>
              </select>
            </div>
            <div>
              <label className="overline text-navy/60">Contact person</label>
              <input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} data-testid="ai-contact"
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="overline text-navy/60">Tie to a deal (optional)</label>
              <select value={dealId} onChange={(e) => setDealId(e.target.value)} data-testid="ai-deal"
                className="w-full mt-2 border border-navy/15 bg-transparent py-2 px-3 focus:outline-none focus:border-gold text-sm">
                <option value="">— none —</option>
                {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="overline text-navy/60">Extra context (optional)</label>
              <textarea rows={3} value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="ai-extra"
                placeholder="e.g., 'Mention that we co-invested with their portfolio company last year.'"
                className="w-full mt-2 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm" />
            </div>
            <div className="pt-4 flex justify-end gap-3">
              <button onClick={onClose} className="text-xs uppercase tracking-widest text-navy/60 hover:text-navy">Cancel</button>
              <button onClick={draft} disabled={busy} data-testid="ai-draft-submit"
                className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-50 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" /> {busy ? "Drafting…" : "Draft email"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="ai-draft-output">
            <div className="bg-cream border border-navy/10 p-4">
              <div className="overline text-gold-deep mb-1">Subject</div>
              <div className="text-navy font-medium">{drafted.subject}</div>
              <div className="gold-rule my-3" />
              <div className="overline text-gold-deep mb-1">Body</div>
              <pre className="whitespace-pre-wrap text-sm text-navy/85 font-sans">{drafted.body}</pre>
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <button onClick={() => setDrafted(null)} className="text-xs uppercase tracking-widest text-navy/60 hover:text-navy" data-testid="ai-redraft">
                ← Redraft
              </button>
              <button onClick={copy} className="border border-navy text-navy px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold flex items-center gap-2" data-testid="ai-copy">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button onClick={logAsSent} className="bg-navy text-gold px-5 py-2 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2" data-testid="ai-log-sent">
                <Send className="h-3.5 w-3.5" /> Log as sent
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Log Outreach modal (manual)
// ────────────────────────────────────────────────────────────────────
function LogOutreachModal({ investor, deals, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? {
    deal_id: initial.deal_id || "",
    contact_person: initial.contact_person || "",
    channel: initial.channel || "Email",
    status: initial.status || "Sent",
    direction: initial.direction || "Outbound",
    subject: initial.subject || "",
    body: initial.body || "",
    response_summary: initial.response_summary || "",
    warm_intro_source: initial.warm_intro_source || "",
    last_touch_at: initial.last_touch_at?.slice(0, 10) || "",
    next_touch_at: initial.next_touch_at?.slice(0, 10) || "",
    notes: initial.notes || "",
  } : {
    deal_id: (investor.deal_ids || [])[0] || "",
    contact_person: (investor.contact_persons || [])[0]?.name || "",
    channel: "Email",
    status: "Sent",
    direction: "Outbound",
    subject: "",
    body: "",
    response_summary: "",
    warm_intro_source: "",
    last_touch_at: new Date().toISOString().slice(0, 10),
    next_touch_at: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        deal_id: form.deal_id || null,
        last_touch_at: form.last_touch_at ? new Date(form.last_touch_at).toISOString() : null,
        next_touch_at: form.next_touch_at ? new Date(form.next_touch_at).toISOString() : null,
      };
      if (initial) await api.put(`/outreach/${initial.id}`, payload);
      else await api.post(`/investors/${investor.id}/outreach`, payload);
      toast.success(initial ? "Updated" : "Logged");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/60 flex items-center justify-center p-4" onClick={onClose} data-testid="log-outreach-modal">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-gold-deep">{initial ? "Edit outreach" : "Log outreach"}</div>
            <h3 className="font-serif text-2xl text-navy mt-1">{investor.name}</h3>
          </div>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="h-5 w-5" /></button>
        </div>
        <div className="gold-rule mb-5" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["channel", "Channel", ["Email", "LinkedIn", "Phone", "Warm intro", "Meeting"]],
            ["status", "Status", ["Not Started", "Sent", "Responded", "Meeting Booked", "Passed", "Won"]],
            ["direction", "Direction", ["Outbound", "Inbound"]],
          ].map(([k, l, opts]) => (
            <div key={k}>
              <label className="overline text-navy/60">{l}</label>
              <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`outreach-${k}`}
                className="w-full mt-2 border border-navy/15 bg-transparent py-2 px-3 focus:outline-none focus:border-gold text-sm">
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ))}
          <div>
            <label className="overline text-navy/60">Tie to a deal</label>
            <select value={form.deal_id} onChange={(e) => setForm({ ...form, deal_id: e.target.value })} data-testid="outreach-deal"
              className="w-full mt-2 border border-navy/15 bg-transparent py-2 px-3 focus:outline-none focus:border-gold text-sm">
              <option value="">— none —</option>
              {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline text-navy/60">Contact person</label>
            <input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="outreach-contact"
              className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="overline text-navy/60">Warm intro source</label>
            <input value={form.warm_intro_source} onChange={(e) => setForm({ ...form, warm_intro_source: e.target.value })}
              className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="overline text-navy/60">Last touch</label>
            <input type="date" value={form.last_touch_at} onChange={(e) => setForm({ ...form, last_touch_at: e.target.value })}
              className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="overline text-navy/60">Next touch</label>
            <input type="date" value={form.next_touch_at} onChange={(e) => setForm({ ...form, next_touch_at: e.target.value })}
              className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-navy/60">Subject</label>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="outreach-subject"
              className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-navy/60">Body / message</label>
            <textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="outreach-body"
              className="w-full mt-2 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-navy/60">Response summary</label>
            <textarea rows={2} value={form.response_summary} onChange={(e) => setForm({ ...form, response_summary: e.target.value })}
              className="w-full mt-2 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-navy/60">Internal notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full mt-2 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm" />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-navy/10">
          <button onClick={onClose} className="text-xs uppercase tracking-widest text-navy/60 hover:text-navy">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="outreach-save"
            className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-50">
            {busy ? "Saving…" : initial ? "Save changes" : "Log outreach"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// AI Shortlist modal — score investors against a deal
// ────────────────────────────────────────────────────────────────────
function AIShortlistModal({ deals, onClose, onLinked }) {
  const [dealId, setDealId] = useState(deals[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!dealId) return toast.error("Pick a deal first");
    setBusy(true);
    try {
      const { data } = await api.post("/investors/ai-shortlist", { deal_id: dealId, limit: 10 });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Shortlist failed");
    } finally {
      setBusy(false);
    }
  };

  const link = async (inv) => {
    await api.post(`/investors/${inv.id}/link-deal/${dealId}`);
    toast.success(`${inv.name} linked to deal`);
    onLinked();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/60 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-shortlist-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-gold-deep">Investor Shortlist</div>
            <h3 className="font-serif text-2xl text-navy mt-1">Score against a deal</h3>
          </div>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="h-5 w-5" /></button>
        </div>
        <div className="gold-rule mb-5" />

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="overline text-navy/60">Deal</label>
            <select value={dealId} onChange={(e) => setDealId(e.target.value)} data-testid="ai-shortlist-deal"
              className="w-full mt-2 border border-navy/15 bg-transparent py-2 px-3 focus:outline-none focus:border-gold text-sm">
              {deals.map((d) => <option key={d.id} value={d.id}>{d.name} — {d.sector} · {d.country}</option>)}
            </select>
          </div>
          <button onClick={run} disabled={busy} data-testid="ai-shortlist-run"
            className="bg-navy text-gold px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-50 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> {busy ? "Scoring…" : "Generate shortlist"}
          </button>
        </div>

        {result && (
          <div className="mt-6 space-y-2" data-testid="ai-shortlist-results">
            <div className="overline text-navy/60 mb-2">Top {result.shortlist.length} matches</div>
            {result.shortlist.map((inv, idx) => (
              <div key={inv.id} className="bg-cream border border-navy/10 p-4 flex items-start justify-between gap-3" data-testid={`shortlist-${inv.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-navy">#{idx + 1} {inv.name}</span>
                    <PriorityPill p={inv.priority} />
                    <span className="text-[10px] uppercase tracking-widest text-gold-deep bg-gold/15 px-2 py-1">
                      Score {inv.fit_match_score}
                    </span>
                  </div>
                  <div className="text-xs text-navy/55 mt-1">{inv.investor_type} · {inv.hq_city}, {inv.hq_country?.split(",")[0]}</div>
                  <div className="text-xs text-navy/65 mt-1 truncate">Sector: {inv.sector_fit || "—"}</div>
                </div>
                <button onClick={() => link(inv)} data-testid={`shortlist-link-${inv.id}`}
                  className="text-xs uppercase tracking-widest text-gold-deep hover:underline whitespace-nowrap">
                  + Link to deal
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Import XLSX modal — bulk-upload a new Investorlist export
// ────────────────────────────────────────────────────────────────────
function ImportXlsxModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return toast.error("Pick a file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/investors/import-xlsx?update_existing=${updateExisting}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      toast.success(
        `Imported ${data.created} new${data.updated ? `, updated ${data.updated}` : ""}` +
        (data.skipped_already_existing ? `, skipped ${data.skipped_already_existing}` : ""),
      );
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/60 flex items-center justify-center p-4" onClick={onClose} data-testid="import-xlsx-modal">
      <div className="bg-white max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-gold-deep">Bulk Import</div>
            <h3 className="font-serif text-2xl text-navy mt-1">Investorlist XLSX</h3>
          </div>
          <button onClick={onClose} className="text-navy/40 hover:text-navy" data-testid="import-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="gold-rule mb-5" />

        {!result ? (
          <div className="space-y-5">
            <p className="text-sm text-navy/70 font-light leading-relaxed">
              Drop in your latest Investorlist export. The sheet must be named{" "}
              <code className="text-xs bg-cream px-1.5 py-0.5">Enriched_Investor_Master</code>{" "}
              with the standard column headers (Investor Name, Investor Type, HQ, Stage Fit, Sector Fit, etc.).
            </p>

            <label
              className="block border-2 border-dashed border-navy/20 hover:border-gold p-8 text-center cursor-pointer transition-colors"
              data-testid="import-dropzone"
            >
              <Upload className="h-8 w-8 mx-auto text-gold-deep mb-2" />
              <div className="text-sm text-navy/85">
                {file ? file.name : "Click to choose a file"}
              </div>
              <div className="text-xs text-navy/45 mt-1">
                {file ? `${(file.size / 1024).toFixed(1)} KB` : ".xlsx or .xlsm — up to 10 MB"}
              </div>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="import-file-input"
              />
            </label>

            <label className="flex items-start gap-3 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-gold"
                data-testid="import-update-existing"
              />
              <div>
                <div className="text-navy font-medium">Update existing investors</div>
                <div className="text-xs text-navy/55 mt-0.5">
                  If a matching name is found, refresh its fields from the spreadsheet. Linked deals,
                  contact-person emails/phones, and created-on date are preserved.
                </div>
              </div>
            </label>

            <div className="pt-4 flex justify-end gap-3 border-t border-navy/10">
              <button onClick={onClose} className="text-xs uppercase tracking-widest text-navy/60 hover:text-navy">Cancel</button>
              <button
                onClick={upload}
                disabled={!file || busy}
                data-testid="import-submit"
                className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-50 flex items-center gap-2"
              >
                <Upload className="h-3.5 w-3.5" /> {busy ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="import-result">
            <div className="bg-cream border border-navy/10 p-4">
              <div className="overline text-gold-deep mb-2">Import complete</div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="font-serif text-3xl text-navy">{result.created}</div>
                  <div className="text-[10px] uppercase tracking-widest text-navy/55">Created</div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-navy">{result.updated}</div>
                  <div className="text-[10px] uppercase tracking-widest text-navy/55">Updated</div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-navy/50">{result.skipped_already_existing}</div>
                  <div className="text-[10px] uppercase tracking-widest text-navy/55">Skipped</div>
                </div>
              </div>
              <div className="text-xs text-navy/55 mt-3">
                Sheet: <code className="bg-white px-1">{result.sheet}</code> · Rows processed: {result.total_data_rows}
              </div>
            </div>
            {(result.errors || []).length > 0 && (
              <div className="bg-amber-50 border-l-2 border-amber-400 p-3 max-h-32 overflow-y-auto">
                <div className="overline text-amber-700 mb-1">Errors</div>
                <ul className="text-xs text-amber-900 space-y-0.5">
                  {result.errors.map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                data-testid="import-done"
                className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
